/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import Container from "../components/Container";
import Title from "../components/ui/Title";
import Stack from "../components/ui/Stack";
import DetailsForm from "../components/booking/DetailsForm";
import OrderDetails from "../components/booking/OrderDetails";
import usePluginUtils from "../hooks/usePluginUtils";
import ClientAuth from "../components/booking/ClientAuth";
import LoadingIndicator from "../components/LoadingIndicator";
import { useHeldHeight } from "../hooks/useHeldHeight";
import { useAuth } from "../hooks/useAuth";
import { useBooking } from "../hooks/useBooking";
import { useBookingSummary } from "../hooks/useBookingSummary";
import { useBookingMutation } from "../hooks/useBookingMutation";
import { useStepManager } from "../hooks/useStepManager";
import { useAuthSuccessHandler } from "../hooks/useAuthSuccessHandler";
import { useClientTerms } from "../lib/react-query/useClientTerms";
import { useAdditionalFields } from "../lib/react-query/useAdditionalFields";
import { buildProductsForRequest } from "../store/slices/bookingSlice";
import NavigationHeader from "../components/NavigationHeader";
import { isClassService } from "../utils/classes";

const StyledTitle = styled((props) => <Title {...props} />)`
  && {
    margin-bottom: 8px;
  }
`;

const Details = () => {
  const { selectedServices, selectedProvider, bookingRequest, updateBooking } = useBooking();
  const { client } = useAuth();
  const form = useForm({
    defaultValues: {
      name: client?.name ?? "",
      email: client?.email ?? "",
      phone: client?.phone ?? "",
    },
  });
  const { isPluginEnabled } = usePluginUtils();
  const clientLoginEnabled = isPluginEnabled("client_login");
  const depositEnabled = isPluginEnabled("deposit_paid_events");
  const { total, taxes, currency, deposit } = useBookingSummary();
  const { handleBookingMutation, isPending } = useBookingMutation();
  const { navigateToNextStep } = useStepManager();
  const { data: terms, isLoading: termsLoading } = useClientTerms();
  const { data: additional_fields, isLoading: fieldsLoading } = useAdditionalFields(
    { "filter[service_id]": selectedServices.map((service) => service.id) },
    { enabled: isPluginEnabled("event_field") }
  );

  const hasAdditionalFields = isPluginEnabled("event_field") && additional_fields?.length > 0;
  const packageEnabled = isPluginEnabled("package");
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const heldHeight = useHeldHeight(isAutoSubmitting);

  const handleSubmitForm = (data, captchaToken, refreshChallenge) => {
    const clientData = { name: data.name, email: data.email, phone: data.phone };

    if (!clientLoginEnabled) {
      updateBooking({ client: clientData });
    }

    if (hasAdditionalFields) {
      navigateToNextStep();
      return;
    }

    const formData = client && clientLoginEnabled ? {} : { client: clientData };
    const bookingObject = {
      ...bookingRequest,
      ...formData,
      products: buildProductsForRequest(selectedServices),
      ...(captchaToken ? { captcha_token: captchaToken } : {}),
    };

    const handleError = (...args) => {
      setIsAutoSubmitting(false);
      form.setError(...args);
    };
    handleBookingMutation(bookingObject, handleError, { onError: refreshChallenge });
  };

  const handleAuthSuccess = useAuthSuccessHandler({
    form,
    onSubmit: handleSubmitForm,
    setIsAutoSubmitting,
  });

  if (termsLoading || fieldsLoading) {
    return (
      <Container>
        <NavigationHeader location back="step" />
        <StyledTitle size="h2">Booking details</StyledTitle>
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader location back="step" />
      <StyledTitle size="h2">Booking details</StyledTitle>
      <Stack $gap="1.5rem">
        <OrderDetails
          showTimeCard
          canEdit
          recurring={bookingRequest.recurring}
          services={selectedServices}
          provider={isClassService(selectedServices[0]) ? null : selectedProvider}
          price={total}
          taxes={taxes}
          currency={currency}
          deposit={depositEnabled ? deposit : null}
        />
        <div ref={heldHeight.ref} style={heldHeight.style}>
          {isAutoSubmitting && <LoadingIndicator page />}
          {!isAutoSubmitting && clientLoginEnabled && !client && (
            <ClientAuth onAuthSuccess={handleAuthSuccess} />
          )}
          {!isAutoSubmitting && (client || !clientLoginEnabled) && (
            <DetailsForm
              form={form}
              terms={terms}
              onSubmit={handleSubmitForm}
              submitTitle={hasAdditionalFields || packageEnabled ? "continue" : "book_btn_title"}
              isSubmitting={isPending}
              withCaptcha={!hasAdditionalFields}
            />
          )}
        </div>
      </Stack>
    </Container>
  );
};

export default Details;
